import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Terminal, ExternalLink } from "lucide-react";
import links from "../data/links";

const COMMANDS = [
  {
    id: "install",
    label: "01 · Install",
    cmd: "pip install prometheus-ebm",
  },
  {
    id: "run",
    label: "02 · Run a deep_probe",
    cmd: "prometheus-ebm run --mode deep_probe --model anthropic/claude-opus-4.7",
  },
  {
    id: "run-extended",
    label: "03 · Or an extended sweep",
    cmd: "prometheus-ebm run --mode extended --models opus-4.7 gpt-5.4 gemini-3.1",
  },
];

function CommandRow({ c }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(c.cmd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div
      className="border border-white/10 bg-[#0a0a0a] hover:border-[#FF4D00]/50 transition-colors group"
      data-testid={`command-row-${c.id}`}
    >
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-[#0d0d0d]">
        <div className="flex items-center gap-3">
          <Terminal className="w-3.5 h-3.5 text-[#FF4D00]" />
          <span className="font-mono text-[10px] tracking-[0.3em] text-neutral-500 uppercase">
            {c.label}
          </span>
        </div>
        <button
          onClick={copy}
          data-testid={`copy-${c.id}`}
          className="font-mono text-[10px] tracking-[0.2em] uppercase flex items-center gap-1.5 text-neutral-500 hover:text-[#FF4D00] transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" /> copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" /> copy
            </>
          )}
        </button>
      </div>
      <div className="px-5 py-5 font-mono text-sm text-neutral-200 overflow-x-auto">
        <span className="text-[#FF4D00] select-none mr-2">$</span>
        <span>{c.cmd}</span>
      </div>
    </div>
  );
}

export default function GetStarted() {
  return (
    <section
      id="install"
      data-testid="install-section"
      className="relative py-32 px-6 lg:px-10 border-t border-white/5 bg-[#070707]"
    >
      <div className="absolute inset-0 tech-grid opacity-25 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="chapter-label mb-6">05 · Get Started</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-neutral-100">
            One <span className="text-[#FF4D00]">pip</span> away from honest
            measurement.
          </h2>
          <p className="mt-6 text-neutral-400 leading-relaxed">
            Ships with the V5 protocol, the full 1,459-item anti-contamination
            dataset, and reproducible scoring. No hidden API. No paywall.
          </p>

          <div className="mt-8 flex flex-col gap-3">
            <a
              href={links.pypi}
              target="_blank"
              rel="noreferrer"
              data-testid="link-pypi"
              className="group flex items-center justify-between border border-white/10 p-4 hover:border-[#FF4D00]/60 transition-colors"
            >
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-neutral-400 group-hover:text-neutral-100">
                PyPI · prometheus-ebm
              </span>
              <ExternalLink className="w-4 h-4 text-neutral-600 group-hover:text-[#FF4D00]" />
            </a>
            <a
              href={links.kaggle_dataset}
              target="_blank"
              rel="noreferrer"
              data-testid="link-kaggle-dataset"
              className="group flex items-center justify-between border border-white/10 p-4 hover:border-[#00F0FF]/60 transition-colors"
            >
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-neutral-400 group-hover:text-neutral-100">
                Kaggle · Dataset
              </span>
              <ExternalLink className="w-4 h-4 text-neutral-600 group-hover:text-[#00F0FF]" />
            </a>
            <a
              href={links.github_sdk}
              target="_blank"
              rel="noreferrer"
              data-testid="link-github-sdk"
              className="group flex items-center justify-between border border-white/10 p-4 hover:border-[#FF4D00]/60 transition-colors"
            >
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-neutral-400 group-hover:text-neutral-100">
                GitHub · SDK
              </span>
              <ExternalLink className="w-4 h-4 text-neutral-600 group-hover:text-[#FF4D00]" />
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7 space-y-4"
        >
          {COMMANDS.map((c) => (
            <CommandRow key={c.id} c={c} />
          ))}

          <div className="pt-4 text-[11px] font-mono tracking-widest text-neutral-600">
            REQUIRES · PYTHON ≥ 3.10 · MODEL PROVIDER API KEY
          </div>
          <div className="mt-6 border border-white/10 bg-[#0a0a0a] p-5 border-l-2 border-l-[#FF4D00]">
            <div className="font-mono text-[10px] tracking-widest text-[#FF4D00] uppercase mb-2">Note on compute requirements</div>
            <p className="text-neutral-400 text-[13px] leading-relaxed">
              Running a full <span className="font-mono text-[#FF4D00]">DEEP_PROBE</span> evaluates 1,459 claims across 4 solvency classes. This requires approximately $35-$50 in API credits depending on the model's cost per token.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
